import { useState } from "react"
import { useMutation } from "react-query"

import { buildLaunchCart } from "src/api/DryStack"

import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

import { fillUpAddOns } from "../LaunchFuelQuantityModal"

// The launches page runs on a hash router and POS runs on its own browser
// router under /point_of_sale, so react-router cannot carry the dockhand
// across. useServerCart reads cart_id off the query string on the way in.
const useLaunchCart = ({ marinaSlug, trackingProps, genericError }) => {
  const showToast = useToast()
  const tracker = useTracker()

  const [pendingFuelLaunch, setPendingFuelLaunch] = useState(null)

  const { mutate, isLoading } = useMutation(
    ({ encodedId, fuelQuantities }) =>
      buildLaunchCart(marinaSlug, encodedId, fuelQuantities),
    {
      onSuccess: (data) => {
        setPendingFuelLaunch(null)
        window.location.assign(
          `/manage/${marinaSlug}/point_of_sale?cart_id=${data.cart.id}`
        )
      },
      onError: (error) => {
        tracker.trackEvent("dry_stack_schedule_view:launch_cart_build_failed", {
          ...trackingProps,
          reason: error?.message,
        })
        showToast(error?.message || genericError, { type: "error" })
      },
    }
  )

  const buildCart = (row) => {
    if (isLoading) return

    tracker.trackEvent("dry_stack_schedule_view:launch_cart_started", {
      ...trackingProps,
    })

    // A fill-up carries no amount until the pump stops, and the server refuses
    // to price one. Ask before posting rather than round-tripping to a 422 the
    // dockhand has no way to answer.
    if (fillUpAddOns(row.addons).length > 0) {
      setPendingFuelLaunch(row)
      return
    }

    mutate({ encodedId: row.encodedId, fuelQuantities: [] })
  }

  const confirmFuelQuantities = (fuelQuantities) => {
    if (isLoading || !pendingFuelLaunch) return

    tracker.trackEvent("dry_stack_schedule_view:launch_cart_fuel_measured", {
      ...trackingProps,
    })
    mutate({ encodedId: pendingFuelLaunch.encodedId, fuelQuantities })
  }

  const cancelFuelQuantities = () => setPendingFuelLaunch(null)

  return {
    buildCart,
    cancelFuelQuantities,
    confirmFuelQuantities,
    isBuildingCart: isLoading,
    pendingFuelLaunch,
  }
}

export default useLaunchCart
